import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import React, { useEffect, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Settings2, ExternalLink, AlertTriangle, ShieldCheck, ShieldAlert, Loader2, Save, PencilLine, Wand2, History, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import EngagementSettingsDialog from "@/components/EngagementSettingsDialog";
import { useAuth } from "@/contexts/AuthContext";
import { fwName } from "@/lib/fwNames";
import { fmtDateTimeFirm, fmtDate } from "@/lib/dt";
import SourceBadge from "@/components/SourceBadge";
import PriorSourceLabel from "@/components/PriorSourceLabel";   // B35-2
import LayerLine from "@/components/LayerLine";   // Batch 32 · B32-1 — the layer sentence (was the LayerBadge pill)
import { soceFootnoteKeys } from "@/lib/opsFmt";   // Batch 8.2 · F-41
import { cfLabel, pairWithPrior, InterestAssumedNote } from "@/pages/IFRSCashFlowParts";   // Batch 31 · items 20 + 31
import { CashReconRows, OverdraftNote, DiscontinuedCashNote, CashFlowWarnings, TieHeadsNote } from "@/pages/IFRSCashFlowItems";   // IFRS 18 / CF card V2
import CashFlowInfo from "@/pages/CashFlowInfo";   // STEP 7d · owner decision: the cash-flow INFO lines (not warnings)
import IFRSCashFlowTbInputs from "@/pages/IFRSCashFlowTbInputs";   // STEP 11b-2 · F2: the figures a trial balance cannot hold
import { PLRenderer, Ifrs18FirstYearToggle, TransitionNote, noteTitle } from "@/pages/IFRSPlParts";   // IFRS 18 / CF card V2 · Part A
import MpmPanel, { MpmNoteView, mpmText } from "@/components/MpmPanel";   // IFRS 18 / CF card V2 · A8
import { RestatedProvider, RestatedPanel, RestatedTag, RestatementNoteView } from "@/components/restatement/RestatedStatements";   // Restatement Phase 3a
import { thirdOf, ThirdHead, thirdLines, amountIn, regenNeededText, DeferredTaxOffsetText, FsPresentationOptions } from "@/components/restatement/SfpThirdColumn";   // Batch 34 · fork B
import RestatementFlagClear from "@/components/restatement/RestatementFlagClear";   // PR 53 · F9
import FirstYearConflictNote from "@/components/FirstYearConflictNote";   // SUNDAY BATCH · FX2-2
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter
import PeriodEndNote from "@/components/PeriodEndNote";   // STEP 3 · B-7b
import { NotesNotPresented, shownNo } from "@/components/NoteNumbers";   // Step 12-2 · B: running note numbers, "Not presented"
import { NoteFooter, NoteLedgerMovements, cfNetLabel, noteTieFailed } from "@/components/notes/NoteTies";   // Step 12-1 · A0–A5
import FsExportMenu, { PubKind } from "@/components/FsExportMenu";   // Step 12-3 · C: one Export button (Excel / Word / PDF)
import IfrsUpdating, { statementsStored, useIfrsStoredPoll } from "@/components/ifrs/IfrsStoredRefresh";   // Speed Card V1 · S2

// Batch 8.1 · F-37 (never "-0.00") and Batch 6 · B (AT-7, never "3,421,324.5"): both inside the one formatter (B-2e)
const fmt = (n) => fmtNumber(Number(n ?? 0));

export default function IFRS() {
  const { t, lang, dict } = useI18n();
  const { current, refresh } = useEngagement();
  const { user } = useAuth();
  const [tab, setTab] = useState("is");
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Legacy renderer state (pre-FS-module engagements).
  const [legacyIs, setLegacyIs] = useState(null);
  const [legacyBs, setLegacyBs] = useState(null);
  const [unmapped, setUnmapped] = useState(null);

  // New Pass 1B renderer state.
  const [sfp, setSfp] = useState(null);
  const [pl, setPl] = useState(null);
  const [pass1bError, setPass1bError] = useState("");
  const [preflight, setPreflight] = useState(null);

  // Pass 1C renderer state.
  const [soce, setSoce] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [validation, setValidation] = useState(null);
  const [notes, setNotes] = useState(null);

  // Pass 3 — Publication PDF state (Step 12-3 · C: the export itself is FsExportMenu).
  const [pubHistory, setPubHistory] = useState([]);
  const [pubHistoryOpen, setPubHistoryOpen] = useState(false);

  const framework = current?.presentation_framework || "";

  // Step 7c · E3: the statements load when the engagement / framework changes (as before) — t is read for the error texts only
  const loadStatements = useEffectEvent(() => {
    if (!current) return;
    setLegacyIs(null); setLegacyBs(null); setSfp(null); setPl(null); setPass1bError(""); setPreflight(null);
    setSoce(null); setCashFlow(null); setValidation(null); setNotes(null);
    api.get(`/coa/${current.id}/unmapped`).then((r) => setUnmapped(r.data)).catch(() => {});
    if (framework) {
      // New FS module renderers.
      api.get(`/fs/${current.id}/sfp`).then((r) => setSfp(r.data))
        .catch((e) => setPass1bError(toErrMsg(e, t("ifrs.loadFailedSfp"))));
      api.get(`/fs/${current.id}/pl`).then((r) => setPl(r.data))
        .catch((e) => setPass1bError(toErrMsg(e, t("ifrs.loadFailedPl"))));
      api.get(`/fs/${current.id}/signoff-preflight`).then((r) => setPreflight(r.data)).catch(() => {});
      // Pass 1C fetches (no toast on error — renderers show inline states).
      api.get(`/fs/${current.id}/soce`).then((r) => setSoce(r.data)).catch(() => {});
      api.get(`/fs/${current.id}/cash-flow`).then((r) => setCashFlow(r.data)).catch(() => {});
      api.get(`/fs/${current.id}/validation`).then((r) => setValidation(r.data)).catch(() => {});
      api.get(`/fs/${current.id}/notes`).then((r) => setNotes(r.data)).catch(() => {});
    } else {
      // Legacy renderers for pre-migration engagements (§9 AC8).
      api.get(`/ifrs/${current.id}/income-statement`).then((r) => setLegacyIs(r.data));
      api.get(`/ifrs/${current.id}/balance-sheet`).then((r) => setLegacyBs(r.data));
    }
  });
  const engId = current?.id;
  useEffect(() => { loadStatements(); }, [engId, framework, loadStatements]);
  // Speed Card V1 · S2: the last built statements labelled "Updating…" while the current ones are built — reloaded until current
  const stored = statementsStored(sfp, pl, soce, cashFlow, notes);
  useIfrsStoredPoll(framework ? engId : null, stored, { sfp: setSfp, pl: setPl, soce: setSoce, "cash-flow": setCashFlow, notes: setNotes });

  useEffect(() => {
    if (!engId || !framework) return;
    api.get(`/fs/${engId}/publication-history`)
      .then((r) => setPubHistory(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [engId, framework]);

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;

  const fy = current.financial_year;
  const py = String(Number(fy) - 1);
  // Silent defaults · A-15: a missing presentation framework blocks the export too (server: allowed false + code)
  const exportBlocked = preflight && !preflight.allowed;
  const exportReason = exportBlocked ? ((lang === "ar" && preflight.reason_ar) || preflight.reason) : undefined;

  // Step 12-3 · C / D13: after ANY export (Excel, Word, PDF) the page re-reads the publication trail and the engagement, so
  // the "Regenerate needed" banner goes at once (the server cleared the marker) — no reload
  const onExported = async () => {
    try {
      const r = await api.get(`/fs/${current.id}/publication-history`);
      setPubHistory(Array.isArray(r.data) ? r.data : []);
    } catch { /* the trail panel keeps what it showed */ }
    await refresh();
  };

  return (
    <RestatedProvider engId={current.id}>
    <div className="space-y-6" data-testid="ifrs-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("ifrs.title")} <SourceBadge source={pl?.source || sfp?.source} className="ms-2 align-middle" /> <PriorSourceLabel source={pl?.prior_source || sfp?.prior_source} className="ms-1 align-middle" /></h1>
          <LayerLine screen="ifrs" />
          <PeriodEndNote engagement={current} />
          {/* STEP 11b · F9: "Loading…" until the P&L or SFP reply names the source (before: "from the Trial Balance upload (no GL loaded)" while a GL engagement loaded) */}
          <p className="text-sm text-muted-foreground" data-testid="ifrs-subtitle">{framework && !pl && !sfp && !pass1bError ? t("common.loading") : t("ifrs.subtitleSrc").replace("{source}", (pl?.source || sfp?.source) === "gl" ? t("analytics.sourceGl") : t("analytics.sourceTb"))}</p>
          <IfrsUpdating stored={stored} t={t} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {framework && (
            <Link to={`/ifrs/${current.id}/mapping`} data-testid="ifrs-open-mapping">
              <Button variant="outline" size="sm">
                <ExternalLink className="me-1 h-3.5 w-3.5" /> {t("ifrs.fsMappingBtn")}
              </Button>
            </Link>
          )}
          {framework && (
            <FsExportMenu engagement={current} user={user} disabled={!!exportBlocked} title={exportReason}
                          onExported={onExported} onBlocked={() => setTab("validation")} />
          )}
          {framework && pubHistory.length > 0 && (
            <div className="relative">
              <Button variant="ghost" size="sm"
                      onClick={() => setPubHistoryOpen((v) => !v)}
                      data-testid="ifrs-pub-history-btn">
                <History className="me-1 h-3.5 w-3.5" />
                {fmtPlural(pubHistory.length, lang, dict.plurals.exports)}
              </Button>
              {pubHistoryOpen && (
                <div className="absolute end-0 top-full z-20 mt-1 w-80 rounded-md border border-slate-200 bg-white p-2 text-xs shadow-lg dark:border-slate-800 dark:bg-slate-900"
                     data-testid="ifrs-pub-history-panel">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("ifrs.publicationAuditTrail")}
                  </div>
                  <ul className="max-h-60 space-y-1 overflow-auto">
                    {pubHistory.map((h) => (
                      <li key={h.id} className="rounded border border-slate-100 p-1.5 dark:border-slate-800"
                          data-testid={`ifrs-pub-history-row-${h.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <PubKind row={h} />
                          <span className="text-[10px] text-muted-foreground">
                            {fmtDateTimeFirm(h.generated_at)}
                          </span>
                        </div>
                        <div className="mt-0.5 break-words text-[10px]">
                          {h.generated_by_name || t("ifrs.unknownUser")} · {(h.byte_size / 1024).toFixed(1)} KB
                        </div>
                        <div className="mt-0.5 truncate font-mono text-[9px] text-slate-500"
                             title={h.sha256}>SHA-256 {h.sha256?.slice(0, 24)}…</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Framework NOT set — banner for legacy engagements (§9 AC8). */}
      {!framework && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-800 dark:bg-amber-950/30"
             data-testid="ifrs-framework-missing">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-700" />
            <div className="flex-1 space-y-1">
              <div className="font-semibold text-amber-900 dark:text-amber-100">
                {t("ifrs.frameworkNotSet")}
              </div>
              <p className="text-xs text-amber-800 dark:text-amber-200/90">
                {t("ifrs.frameworkNotSetBody").split(/\{ias1Bold\}|\{ifrs18Bold\}/).map((chunk, i, arr) => (
                  <React.Fragment key={i}>
                    {chunk}
                    {i === 0 && arr.length > 1 && <b>IAS 1</b>}
                    {i === 1 && arr.length > 2 && <b>IFRS 18</b>}
                  </React.Fragment>
                ))}
              </p>
              <p className="text-[11px] text-amber-800/80 dark:text-amber-200/70">
                {t("ifrs.frameworkEditableUntil")}
              </p>
            </div>
            <Button size="sm"
                    onClick={() => setSettingsOpen(true)}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="ifrs-open-settings">
              <Settings2 className="me-1 h-3.5 w-3.5" /> {t("ifrs.openEngSettings")}
            </Button>
          </div>
        </div>
      )}

      {/* Round 24c · M1 — framework changed after FS generation: old versions kept, regenerate needed. */}
      {framework && current.fs_regenerate_needed && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
             data-testid="ifrs-regenerate-needed">
          <AlertTriangle className="me-1 inline h-3.5 w-3.5" />
          {regenNeededText(t, current.fs_regenerate_needed, () => t("ifrs.regenNeeded")   /* B34-3: a restatement has its own text */
            .replace("{from}", fwName(t, current.fs_regenerate_needed.from, current.period_start_effective))
            .replace(/\{to\}/g, fwName(t, current.fs_regenerate_needed.to || framework, current.period_start_effective))
            .replace("{by}", current.fs_regenerate_needed.by_name || "—")
            .replace("{at}", fmtDateTimeFirm(current.fs_regenerate_needed.at)))}
        </div>
      )}

      {/* Framework label chip when set */}
      {framework && (
        <div className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300"
             data-testid="ifrs-framework-label"
             title={t("ifrs.frameworkTooltip")}>
          {t("ifrs.frameworkLabel")}
          <span className="font-semibold">{framework}</span>
        </div>
      )}
      {framework && <RestatedPanel engId={current.id} />}
      {framework && <FsPresentationOptions engId={current.id} onSaved={() => ["sfp", "notes", "validation"].forEach((k) => api.get(`/fs/${current.id}/${k}`).then((r) => ({ sfp: setSfp, notes: setNotes, validation: setValidation })[k](r.data)).catch(() => {}))} />}

      {/* Unmapped-accounts warning (single source of truth: /coa/{id}/unmapped) */}
      {unmapped && unmapped.count > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm dark:border-red-800 dark:bg-red-950/30"
             data-testid="ifrs-unmapped-warning">
          <XCircle className="h-4 w-4 text-red-700" />
          <div className="flex-1">
            <span className="font-medium text-red-900 dark:text-red-200">
              {fmtPlural(unmapped.count, lang, dict.plurals.accounts)}{t("ifrs.stillUnmapped")}
              {t("ifrs.netBalancePrefix") + fmtNumber((unmapped.unmapped_net_balance || 0))}
            </span>
            <span className="ms-2 text-xs text-red-800 dark:text-red-300">
              {t("ifrs.excludedNote")}
            </span>
          </div>
          <Link to={framework ? `/ifrs/${current.id}/mapping` : "/gl-import"}>
            <Button size="sm" variant="outline" data-testid="ifrs-fix-unmapped">
              {t("ifrs.fixNow")}
            </Button>
          </Link>
        </div>
      )}

      {pass1bError && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200"
             data-testid="ifrs-fs-error">
          {pass1bError}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="is"    data-testid="tab-is">{t("ifrs.incomeStatement")}</TabsTrigger>
          <TabsTrigger value="bs"    data-testid="tab-bs">{t("ifrs.balanceSheet")}</TabsTrigger>
          <TabsTrigger value="soce"  data-testid="tab-soce">{t("ifrs.tabs.soce")}</TabsTrigger>
          <TabsTrigger value="cf"    data-testid="tab-cf">{t("ifrs.tabs.cf")}</TabsTrigger>
          <TabsTrigger value="validation" data-testid="tab-validation">{t("ifrs.tabs.validation")}</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">{t("ifrs.tabs.notes")}</TabsTrigger>
          {framework && <TabsTrigger value="mpm" data-testid="tab-mpm">{mpmText(lang).tab}</TabsTrigger>}
        </TabsList>

        {/* --------------- Income Statement / P&L --------------- */}
        <TabsContent value="is">
          {framework
            ? <PLRenderer pl={pl} engagement={current} FrameworkBadge={FrameworkBadge} UnmappedInlineWarning={UnmappedInlineWarning}
                          onOpenMapping={() => (window.location.href = `/ifrs/${current.id}/mapping`)} />
            : <LegacyIS is_={legacyIs} fy={fy} py={py} t={t} />}
        </TabsContent>

        {/* --------------- Balance Sheet / SFP --------------- */}
        <TabsContent value="bs">
          {framework
            ? <SFPRenderer sfp={sfp} engagement={current} />
            : <LegacyBS bs={legacyBs} fy={fy} py={py} t={t} periodEnd={current.period_end_effective} />}
        </TabsContent>

        <TabsContent value="soce">
          {framework
            ? <SOCERenderer data={soce} engagement={current} />
            : <Card><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="soce-placeholder">
                {t("ifrs.placeholders.soce")}
              </CardContent></Card>}
        </TabsContent>
        <TabsContent value="cf">
          {framework
            ? <CashFlowRenderer data={cashFlow} engagement={current} onChanged={(c, v) => { setCashFlow(c); setValidation(v); }} />
            : <Card><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="cf-placeholder">
                {t("ifrs.placeholders.cf")}
              </CardContent></Card>}
        </TabsContent>
        <TabsContent value="validation">
          {framework
            ? <ValidationRenderer data={validation} engId={current.id} onChanged={() => api.get(`/fs/${current.id}/validation`).then((r) => setValidation(r.data)).catch(() => {})} />
            : <Card><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="validation-placeholder">
                {t("ifrs.placeholders.validation")}
              </CardContent></Card>}
        </TabsContent>
        <TabsContent value="notes">
          {framework
            ? <NotesRenderer data={notes} engagement={current} onReload={() => api.get(`/fs/${current.id}/notes`).then((r) => setNotes(r.data)).catch(() => {})}
                             onFirstYearSaved={() => { refresh && refresh(); api.get(`/fs/${current.id}/notes`).then((r) => setNotes(r.data)).catch(() => {}); }} />
            : <Card><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="notes-placeholder">
                {t("ifrs.placeholders.notes")}
              </CardContent></Card>}
        </TabsContent>
        {framework && <TabsContent value="mpm">{tab === "mpm" && <MpmPanel engagement={current} />}</TabsContent>}
      </Tabs>

      <EngagementSettingsDialog engagement={current}
                                  open={settingsOpen}
                                  onOpenChange={setSettingsOpen}
                                  onSaved={refresh} />
    </div>
    </RestatedProvider>
  );
}

/* ---------- Pass 1B renderers ---------- */

function FrameworkBadge({ framework }) {
  const cls = framework === "IFRS 18"
    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
    : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300";
  return (
    <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ${cls}`}
          data-testid="fs-framework-badge">
      {framework}
    </span>
  );
}

function UnmappedInlineWarning({ list, onOpenMapping }) {
  const { t, lang, dict } = useI18n();
  if (!list || list.length === 0) return null;
  return (
    <div className="mx-4 mt-2 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
         data-testid="fs-inline-unmapped">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />
      <div className="flex-1">
        <div className="font-medium">
          {fmtPlural(list.length, lang, dict.plurals.accounts)}{t("ifrs.accountsExcluded")}
        </div>
        <div className="opacity-80">
          {list.slice(0, 3).map((u) => u.account_code || u.account_name).join(", ")}
          {list.length > 3 ? t("ifrs.andMoreShort").replace("{n}", list.length - 3) : ""}
        </div>
      </div>
      {onOpenMapping && (
        <Button size="sm" variant="outline" onClick={onOpenMapping}
                data-testid="fs-open-mapping-inline">
          {t("ifrs.openMapping")}
        </Button>
      )}
    </div>
  );
}

export function SFPRenderer({ sfp, engagement }) {   // exported for its jest test (B34-6)
  const { t } = useI18n();
  if (!sfp) {
    return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">{t("ifrs.sfpLoading")}</CardContent></Card>;
  }
  const c = sfp.current;
  const p = sfp.prior;
  const third = thirdOf(p);          // B34-6: the third statement of financial position as a third column (else two, unchanged)
  const nums = third ? "grid grid-cols-3 gap-4 text-end" : "grid grid-cols-2 gap-4 text-end";
  const priorAmt = (code) => {
    if (!p) return null;
    for (const s of Object.values(p.sections)) {
      const found = asArray(s.lines).find((l) => l.code === code);
      if (found) return found.amount;
    }
    return 0;
  };
  return (
    <Card data-testid="sfp-renderer">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">
          {engagement.client_name}{t("ifrs.sfpTitleSuffix").replace("{date}", fmtDate(sfp.period_end))}
        </CardTitle>
        <div className="flex items-center gap-2">
          <FrameworkBadge framework={sfp.framework} />
          {c.totals?.balanced
            ? <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" /> {t("ifrs.balanced")}</span>
            : <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 text-xs text-red-700"><XCircle className="h-3 w-3" /> {t("ifrs.notBalanced")}</span>}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <UnmappedInlineWarning list={c.unmapped_accounts}
                                 onOpenMapping={() =>
                                   (window.location.href = `/ifrs/${engagement.id}/mapping`)} />
        <div className="grid grid-cols-2 border-b bg-slate-50 px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
          <div>{t("ifrs.item")}</div>
          <div className={nums}>
            {third ? <ThirdHead third={third} sfp={sfp} /> : <><div>FY{sfp.financial_year}</div>
            <div>FY{Number(sfp.financial_year) - 1}<RestatedTag /></div></>}
          </div>
        </div>
        {[
          "non_current_assets", "current_assets", "current_assets_hfs",
          "equity", "non_current_liabilities", "current_liabilities", "current_liabilities_hfs",
        ].map((k) => {
          const s = c.sections[k];
          const ps = p?.sections?.[k];
          if (!s || s.lines.length === 0) return null;
          const lines = third ? thirdLines(s, ps, third.sections[k]) : s.lines;
          return (
            <div key={k} data-testid={`sfp-section-${k}`}>
              <div className="border-b bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
                {s.label}
              </div>
              {lines.map((l) => (
                <div key={l.code} className="grid grid-cols-2 border-b px-4 py-1.5 text-sm">
                  <div className="text-slate-700 dark:text-slate-300">
                    <span className="me-2 font-mono text-[10px] text-muted-foreground">{l.code}</span>
                    {l.name}
                  </div>
                  <div className={`${nums} num-cell`}>
                    <div>{fmt(l.amount)}</div>
                    <div className="text-muted-foreground">{fmt(priorAmt(l.code))}</div>
                    {third && <div className="text-muted-foreground" data-testid={`sfp-third-${l.code}`}>{fmt(amountIn(third.sections, k, l.code))}</div>}
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-2 border-b bg-slate-50 px-4 py-2 dark:bg-slate-900/40" data-testid={`sfp-section-total-${k}`}>
                <div className="text-sm font-semibold">{t("ifrs.totalPrefix")}{s.label}</div>
                <div className={`${nums} num-cell font-bold`}>
                  <div>{fmt(s.total)}</div>
                  <div className="text-muted-foreground">{fmt(ps?.total || 0)}</div>
                  {third && <div className="text-muted-foreground">{fmt(third.sections[k]?.total || 0)}</div>}
                </div>
              </div>
            </div>
          );
        })}
        <div className="grid grid-cols-2 bg-slate-900 px-4 py-3 text-white" data-testid="sfp-grand-totals">
          <div className="text-sm font-semibold">{t("ifrs.sfpGrandTotal")}</div>
          <div className={`${nums} num-cell font-bold`}>
            <div>{fmt(c.totals.total_assets)} / {fmt(c.totals.equity_and_liabilities)}</div>
            <div className="text-slate-300">
              {fmt(p?.totals?.total_assets || 0)} / {fmt(p?.totals?.equity_and_liabilities || 0)}
            </div>
            {third && <div className="text-slate-300" data-testid="sfp-third-totals">{fmt(third.totals?.total_assets || 0)} / {fmt(third.totals?.equity_and_liabilities || 0)}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------- Legacy renderers (pre-migration engagements) ---------- */

function StatementSection({ title, lines, total, negative = false, hideTotal = false }) {
  const { t } = useI18n();
  return (
    <div className="border-b last:border-b-0">
      <div className="bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
        {title}
      </div>
      {lines.map((l, i) => (
        <div key={i} className="flex items-center justify-between px-4 py-1.5 text-sm border-b last:border-b-0">
          <div className="text-slate-700 dark:text-slate-300"><span className="me-2 text-muted-foreground">{l.code}</span>{l.name}</div>
          <div className="num-cell font-medium">{fmt(l.amount)}</div>
        </div>
      ))}
      {!hideTotal && (
        <div className="flex items-center justify-between bg-slate-50 px-4 py-2 dark:bg-slate-900/40">
          <div className="text-sm font-semibold">{t("ifrs.totalPrefix")}{title}</div>
          <div className={`num-cell font-bold ${negative ? "text-red-600" : ""}`}>{fmt(total)}</div>
        </div>
      )}
    </div>
  );
}

function NoteEditorial({ note, engagementId, saved, editorialByCode, onSaved }) {
  const { t } = useI18n();
  const [content, setContent] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Persistence key: for constants/cross-cutting we use their segment code
  // (e.g. N01.INFO); for rooted notes we use the LINKED_FS_CODE so a single
  // paragraph covers the whole PPE / Cash / etc. note.
  // But for constants/cross-cutting there is no line — fall back to a stable
  // key derived from the note number.
  const codeForApi = note.kind === "rooted"
    ? (note.linked_fs_code || "")
    : (note.title.startsWith("Expenses by nature") ? "N98.OTH"
        : note.note_no === 1 ? "N01.INFO"
        : note.note_no === 2 ? "N02.BASIS"
        : note.note_no === 3 ? "N03.POL"
        : note.note_no === 4 ? "N04.EST"
        : note.note_no === 73 ? "N90.EPS"
        : note.note_no === 74 ? "N91.RPT"
        : note.note_no === 75 ? "N92.FI"
        : note.note_no === 76 ? "N93.COMM"
        : note.note_no === 77 ? "N94.SUB"
        : note.note_no === 78 ? "N95.MPM"
        : note.note_no === 79 ? "N96.SEG"
        : note.note_no === 80 ? "N97.GC"
        : "");
  const savedForNote = editorialByCode?.[codeForApi];

  useEffect(() => {
    setContent(savedForNote?.content || "");
    setDirty(false);
  }, [savedForNote?.content, codeForApi]);

  const draftWithAI = async () => {
    if (!codeForApi) { toast.error(t("ifrs.noteEditorial.noCodeAiDraft")); return; }
    setDrafting(true);
    try {
      const r = await api.post(`/fs/${engagementId}/note-editorial/${codeForApi}/ai-draft`);
      setContent(r.data.draft || "");
      setDirty(true);
      toast.success(t("ifrs.noteEditorial.aiDrafted"));
    } catch (e) {
      toast.error(toErrMsg(e, t("ifrs.noteEditorial.aiDraftFailed")));
    } finally {
      setDrafting(false);
    }
  };

  const save = async () => {
    if (!codeForApi) { toast.error(t("ifrs.noteEditorial.noCodeSave")); return; }
    setSaving(true);
    try {
      const r = await api.post(`/fs/${engagementId}/note-editorial/${codeForApi}`, { content });
      onSaved({
        note_code: codeForApi, content,
        source: r.data.source, updated_at: r.data.updated_at,
        updated_by_name: "You",
      });
      setDirty(false);
      toast.success(t("ifrs.noteEditorial.savedToast"));
    } catch (e) {
      toast.error(toErrMsg(e, t("ifrs.pdfExportFailed")));
    } finally {
      setSaving(false);
    }
  };

  const canDraft = note.kind !== "rooted";  // rooted disclosures are number-led; constants + cross-cutting use narrative
  const savedTag = savedForNote?.source === "ai" ? t("ifrs.noteEditorial.aiUnedited")
                    : savedForNote?.source === "ai_edited" ? t("ifrs.noteEditorial.aiEdited")
                    : savedForNote?.source === "manual" ? t("ifrs.noteEditorial.savedTag") : "";

  return (
    <div className="mb-3 rounded border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/40"
         data-testid={`note-editorial-${note.note_no}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
          <PencilLine className="h-3 w-3" /> {t("ifrs.noteEditorial.header")}
          {savedTag && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium normal-case text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                  data-testid={`note-editorial-source-${note.note_no}`}>
              {savedTag} · {fmtDate(savedForNote?.updated_at)}
            </span>
          )}
          {dirty && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium normal-case text-amber-800">{t("ifrs.noteEditorial.unsaved")}</span>}
        </div>
        <div className="flex items-center gap-2">
          {canDraft && (
            <Button size="sm" variant="outline"
                    onClick={draftWithAI} disabled={drafting || saving}
                    className="h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300"
                    data-testid={`note-editorial-ai-draft-${note.note_no}`}>
              {drafting
                ? <Loader2 className="me-1 h-3 w-3 animate-spin" />
                : <Wand2 className="me-1 h-3 w-3" />}
              {t("ifrs.noteEditorial.draftWithAI")}
            </Button>
          )}
          <Button size="sm"
                  onClick={save} disabled={saving || !dirty}
                  className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`note-editorial-save-${note.note_no}`}>
            {saving ? <Loader2 className="me-1 h-3 w-3 animate-spin" /> : <Save className="me-1 h-3 w-3" />}
            {t("common.save")}
          </Button>
        </div>
      </div>
      <Textarea
        value={content}
        onChange={(e) => { setContent(e.target.value); setDirty(true); }}
        placeholder={canDraft
          ? t("ifrs.noteEditorial.placeholderDraft")
          : t("ifrs.noteEditorial.placeholderCommentary")}
        rows={5}
        className="text-sm"
        data-testid={`note-editorial-textarea-${note.note_no}`}
      />
    </div>
  );
}

function MovementSchedule({ note, engagementId, saved, onSaved }) {
  const { t } = useI18n();
  // saved is the { "note_code|segment_code": {additions, disposals, charge} } map
  const noteKey = note.linked_fs_code || "";
  const [inputs, setInputs] = useState({});   // { segment_code: { additions, disposals, charge } }
  const [savingKey, setSavingKey] = useState(null);
  const mvCols = useResizableColumns({ storageKey: "ifrs-movement", defaults: [200, 120, 120, 120, 120, 120, 120, 90], mins: [120, 96, 96, 96, 96, 96, 96, 60] });

  const seedInputs = useEffectEvent(() => {
    // Seed inputs from saved map for this note
    const next = {};
    note.lines.forEach((l) => {
      const k = `${noteKey}|${l.code}`;
      const s = saved[k];
      if (s) next[l.code] = { additions: s.additions, disposals: s.disposals, charge: s.charge };
    });
    setInputs(next);
  });
  // Step 7c · E3: re-seeded when the note / saved map / key changes (as before), never on a new note.lines array
  useEffect(() => { seedInputs(); }, [note.note_no, saved, noteKey, seedInputs]);

  const setField = (segCode, field, value) => {
    setInputs((prev) => ({
      ...prev,
      [segCode]: {
        additions: 0, disposals: 0, charge: 0,
        ...prev[segCode],
        [field]: Number(value) || 0,
      },
    }));
  };

  const saveSeg = async (segCode) => {
    if (!engagementId) return;
    setSavingKey(segCode);
    const payload = {
      additions: Number(inputs[segCode]?.additions) || 0,
      disposals: Number(inputs[segCode]?.disposals) || 0,
      charge: Number(inputs[segCode]?.charge) || 0,
    };
    try {
      await api.post(`/fs/${engagementId}/note-movements/${noteKey}/${segCode}`, payload);
      const k = `${noteKey}|${segCode}`;
      onSaved(k, { ...payload, updated_at: new Date().toISOString(), updated_by_name: "You" });
      toast.success(t("ifrs.movement.savedFor").replace("{segCode}", segCode));
    } catch (e) {
      toast.error(toErrMsg(e, t("ifrs.pdfExportFailed")));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="mb-3 rounded border border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-900/40"
         data-testid={`note-movement-${note.note_no}`}>
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
        <PencilLine className="h-3 w-3" /> {t("ifrs.movement.title")}
        <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[9px] font-medium normal-case text-teal-800 dark:bg-teal-950/40 dark:text-teal-300">
          {t("ifrs.movement.formula")}
        </span>
      </div>
      <div className="overflow-x-auto">
        <ResizableTable cols={mvCols} className="w-full text-[11px]">
          <thead>
            <tr className="text-start text-[9px] uppercase tracking-wider text-muted-foreground">
              <ResizableTh index={0} className="py-1 pe-2">{t("ifrs.movement.cols.segment")}</ResizableTh>
              <ResizableTh index={1} className="py-1 pe-2 text-end">{t("ifrs.movement.cols.opening")}</ResizableTh>
              <ResizableTh index={2} className="py-1 pe-2 text-end">{t("ifrs.movement.cols.additions")}</ResizableTh>
              <ResizableTh index={3} className="py-1 pe-2 text-end">{t("ifrs.movement.cols.disposals")}</ResizableTh>
              <ResizableTh index={4} className="py-1 pe-2 text-end">{t("ifrs.movement.cols.charge")}</ResizableTh>
              <ResizableTh index={5} className="py-1 pe-2 text-end">{t("ifrs.movement.cols.closing")}</ResizableTh>
              <ResizableTh index={6} className="py-1 pe-2 text-end">{t("ifrs.movement.cols.unexplained")}</ResizableTh>
              <ResizableTh index={7} className="py-1 pe-2"></ResizableTh>
            </tr>
          </thead>
          <tbody>
            {note.lines.map((l) => {
              const opening = l.py;
              const closing = l.cy;
              const inp = inputs[l.code] || { additions: 0, disposals: 0, charge: 0 };
              const explained = opening + Number(inp.additions) - Number(inp.disposals) - Number(inp.charge);
              const unexplained = closing - explained;
              const broken = Math.abs(unexplained) >= 0.01;
              return (
                <tr key={l.code} className="border-t border-slate-200/60 dark:border-slate-800/60"
                    data-testid={`note-movement-row-${l.code}`}>
                  <td className="py-1 pe-2">
                    <div className="font-mono text-[9px] text-muted-foreground">{l.code}</div>
                    <div className="text-[11px]">{l.title}</div>
                  </td>
                  <td className="py-1 pe-2 text-end num-cell">{fmt(opening)}</td>
                  {["additions", "disposals", "charge"].map((f) => (
                    <td key={f} className="py-1 pe-2">
                      <input
                        type="number" step="0.01"
                        value={inp[f] ?? 0}
                        onChange={(e) => setField(l.code, f, e.target.value)}
                        className="h-6 w-24 rounded border border-slate-300 bg-white px-1 text-end text-[11px] dark:border-slate-700 dark:bg-slate-900"
                        data-testid={`note-movement-input-${l.code}-${f}`}
                      />
                    </td>
                  ))}
                  <td className="py-1 pe-2 text-end num-cell">{fmt(closing)}</td>
                  <td className={`py-1 pe-2 text-end num-cell ${broken ? "text-red-700 font-semibold" : "text-emerald-700"}`}
                      data-testid={`note-movement-unexplained-${l.code}`}>
                    {fmt(unexplained)}
                  </td>
                  <td className="py-1 pe-2 text-end">
                    <Button size="sm" variant="outline"
                            onClick={() => saveSeg(l.code)} disabled={savingKey === l.code}
                            className="h-6 px-2 text-[10px]"
                            data-testid={`note-movement-save-${l.code}`}>
                      {savingKey === l.code
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <Save className="h-3 w-3" />}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </ResizableTable>
      </div>
    </div>
  );
}

/* ---------- Pass 1C renderers ---------- */

const soceColDefaults = (n, min) => [min ? 160 : 260, ...Array(n).fill(min ? 100 : 140), min ? 100 : 140];   // Step 11d · add-on: movement, equity columns, total
export function SOCERenderer({ data, engagement }) {   // exported for its jest test (PR 53 · F2)
  const { t, lang } = useI18n();
  const soceCols = useResizableColumns({ storageKey: "ifrs-soce", defaults: soceColDefaults((data?.soce?.columns || []).length), mins: soceColDefaults((data?.soce?.columns || []).length, true) });
  if (!data) {
    return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">{t("ifrs.soce.loading")}</CardContent></Card>;
  }
  const s = data.soce || {};
  const cols = s.columns || [];
  const rows = s.table || s.rows || [];          // Batch 8.3 · A: prior year + current year in one table
  const fyNum = Number(data.financial_year); const pyLabel = Number.isFinite(fyNum) ? String(fyNum - 1) : "";
  const footParts = [];
  if (s.prior_available && s.prior) footParts.push(t("ifrs.soce.yearPrefix").replace("{fy}", pyLabel) + soceFootnoteKeys(s.prior.sources).map((k) => t(k)).join(" "));
  footParts.push((s.prior_available ? t("ifrs.soce.yearPrefix").replace("{fy}", data.financial_year) : "") + soceFootnoteKeys(s.sources).map((k) => t(k)).join(" "));
  if (s.prior_available === false) footParts.push(t("ifrs.soce.priorNotAvailable"));
  return (
    <Card data-testid="soce-renderer">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">
          {engagement.client_name}{t("ifrs.soce.titleSuffix").replace("{fy}", data.financial_year)}
        </CardTitle>
        <FrameworkBadge framework={data.framework} />
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <ResizableTable cols={soceCols} className="w-full text-sm" data-testid="soce-table">
          <thead className="bg-slate-50 dark:bg-slate-900/60">
            <tr>
              <ResizableTh index={0} className="px-3 py-2 text-start text-xs uppercase tracking-wider text-muted-foreground font-medium">{t("ifrs.soce.movement")}</ResizableTh>
              {cols.map((c, ci) => (
                <ResizableTh index={ci + 1} key={c.code} className="px-3 py-2 text-end text-xs uppercase tracking-wider text-muted-foreground font-medium whitespace-nowrap"
                    data-testid={`soce-col-${c.code}`}>
                  <div>{c.label}</div>
                  <div className="font-mono text-[9px] text-slate-500">{c.code}</div>
                </ResizableTh>
              ))}
              <ResizableTh index={cols.length + 1} className="px-3 py-2 text-end text-xs uppercase tracking-wider text-slate-900 dark:text-slate-200 font-semibold">{t("ifrs.soce.total")}</ResizableTh>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const emphasized = ["opening", "tci", "closing", "py_opening", "py_tci", "py_closing"].includes(r.key)
                || ["Opening balance", "Total comprehensive income", "Closing balance"].includes(r.label);
              return (
                <tr key={i}
                    className={`border-t ${emphasized ? "bg-slate-50 dark:bg-slate-900/40 font-semibold" : ""}`}
                    data-testid={`soce-row-${(r.key || r.label).replace(/\s+/g, '-').toLowerCase()}`}>
                  <td className="px-3 py-1.5">
                    {lang === "ar" && r.label_ar ? r.label_ar : r.label}
                    {r.auto && <span className="ms-2 rounded bg-emerald-100 dark:bg-emerald-950/40 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 dark:text-emerald-300"
                                    title={r.note || ""}>{t("ifrs.soce.auto")}</span>}
                  </td>
                  {asArray(r.cells).map((cell, j) => (
                    <td key={j} className="px-3 py-1.5 text-end num-cell">
                      {r.not_available ? <span className="text-xs text-muted-foreground" data-testid="soce-not-available">{(lang === "ar" && r.not_available_text_ar) || r.not_available_text}</span>   /* PR 53 · F2: TB year */
                        : cell === 0 ? <span className="text-muted-foreground">—</span> : fmt(cell)}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 text-end num-cell font-semibold">{r.not_available ? <span className="text-xs font-normal text-muted-foreground">{(lang === "ar" && r.not_available_text_ar) || r.not_available_text}</span> : fmt(r.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </ResizableTable>
        <div className="border-t bg-slate-50 dark:bg-slate-900/40 px-4 py-2 text-[11px] text-muted-foreground" data-testid="soce-footnote">
          {footParts.join(" ")}   {/* Batch 8.2 · F-41 + 8.3 · A: the sources actually used, both years */}
        </div>
      </CardContent>
    </Card>
  );
}

function DaTaggingBanner({ diag, engagementId }) {
  const { t, lang, dict } = useI18n();
  if (!diag || diag.status === "ok" || !engagementId) return null;
  const missing = diag.status === "missing";
  const depCands = diag.dep_untagged_candidates || [];
  const amortCands = diag.amort_untagged_candidates || [];
  const allCands = [...depCands, ...amortCands];
  if (allCands.length === 0) return null;
  const focusLink = (code) =>
    `/ifrs/${engagementId}/mapping?focus=${encodeURIComponent(code || "")}`;
  const palette = missing
    ? "border-red-300 bg-red-50 text-red-900 dark:bg-red-950/30 dark:text-red-200"
    : "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200";
  return (
    <div className={`border-b px-4 py-3 text-xs ${palette}`}
         data-testid="cf-da-banner">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
        <div className="min-w-0 space-y-1">
          <div className="font-semibold">
            {missing
              ? t("ifrs.da.notTagged")
              : t("ifrs.da.partial")}
          </div>
          <div>
            {missing
              ? t("ifrs.da.notTaggedBody")
              : t("ifrs.da.partialBody")}
          </div>
          <div className="pt-1">
            {t("ifrs.cf.currentlyTagged")} <span className="font-mono">N98.DEP × {diag.dep_tagged_count}</span>
            {" · "}<span className="font-mono">N98.AMORT × {diag.amort_tagged_count}</span>
            {" · "}{t("ifrs.cf.cfoAddBackSoFar")} <span className="font-mono">{fmt(diag.dep_amount)}</span>
            {" "}{t("ifrs.cf.depSuffix")}{" + "}<span className="font-mono">{fmt(diag.amort_amount)}</span>{" "}{t("ifrs.cf.amortSuffix")}
          </div>
          <div className="pt-1 font-semibold">
            {fmtPlural(allCands.length, lang, dict.plurals.accounts)}{t("ifrs.da.needReviewSuffix")}
          </div>
          <ul className="ms-3 list-disc space-y-0.5">
            {allCands.slice(0, 10).map((c) => (
              <li key={c.id}>
                <Link
                  to={focusLink(c.account_code)}
                  data-testid={`cf-da-fix-${c.account_code}`}
                  className="underline underline-offset-2 hover:no-underline"
                >
                  <span className="font-mono">{c.account_code}</span>
                  {" · "}{c.account_name}
                  {c.current_note_mapping
                    ? <span className="opacity-70">{t("ifrs.da.currentlyMapped").replace("{mapping}", c.current_note_mapping)}</span>
                    : <span className="opacity-70">{t("ifrs.da.currentlyUntagged")}</span>}
                  <ArrowRight className="ms-1 inline h-3 w-3" />
                </Link>
              </li>
            ))}
            {allCands.length > 10 && (
              <li className="opacity-70">{t("ifrs.da.andMore").replace("{n}", allCands.length - 10)}</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function CashFlowRenderer({ data, engagement, onChanged }) {
  const { t, lang } = useI18n();
  if (!data) {
    return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">{t("ifrs.cf.loading")}</CardContent></Card>;
  }
  const cf = data.cash_flow || {};
  const tied = Math.abs(cf.tie_check || 0) < 1.0;
  const daDiag = data.da_diagnostic || null;
  // Batch 8.3 · B: the prior-year column (same rules one year back); "not available" when the prior year has no opening journal
  const prior = cf.prior_available && cf.prior ? cf.prior : null;
  const fyNum = Number(data.financial_year); const pyLabel = Number.isFinite(fyNum) ? String(fyNum - 1) : "";
  const na = <span className="text-muted-foreground text-xs" data-testid="cf-prior-na">{t("ifrs.cf.priorNotAvailable")}</span>;

  const Row = ({ label, amount, prior: priorAmount, bold = false, testId }) => (
    <div className={`grid grid-cols-[1fr_9rem_9rem] border-b px-4 py-1.5 text-sm ${bold ? "bg-slate-50 dark:bg-slate-900/40 font-semibold" : ""}`}
         data-testid={testId}>
      <div>{label}</div>
      <div className="text-end num-cell">{fmt(amount)}</div>
      <div className="text-end num-cell" data-testid={testId ? `${testId}-py` : undefined}>{prior ? fmt(priorAmount) : (cf.prior_available === false ? "" : "")}</div>
    </div>
  );
  const ColHead = () => (
    <div className="grid grid-cols-[1fr_9rem_9rem] border-b bg-slate-100 dark:bg-slate-900/70 px-4 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground" data-testid="cf-col-head">
      <div />
      <div className="text-end">{t("ifrs.cf.colYear").replace("{fy}", data.financial_year)}</div>
      <div className="text-end">{prior ? <>{t("ifrs.cf.colYear").replace("{fy}", pyLabel)}<RestatedTag /></> : na}</div>
    </div>
  );
  const SectionHeader = ({ title }) => (
    <div className="border-b bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
      {title}
    </div>
  );

  return (
    <Card data-testid="cf-renderer">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">
          {engagement.client_name}{t("ifrs.cf.titleSuffix").replace("{fy}", data.financial_year)}
        </CardTitle>
        <div className="flex items-center gap-2">
          <FrameworkBadge framework={data.framework} />
          {tied
            ? <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"
                    data-testid="cf-tie-badge"><CheckCircle2 className="h-3 w-3" /> {t("ifrs.cf.cashTie")}</span>
            : <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 text-xs text-red-700"
                    data-testid="cf-tie-badge"><XCircle className="h-3 w-3" /> {t("ifrs.cf.cashTieBroken").replace("{n}", fmt(cf.tie_check))}</span>}
          {!tied && cf.re_unexplained ? (   /* Batch 11 · B9 — why, in plain words (never auto-classified as a dividend) */
            <span className="text-xs text-red-700" data-testid="cf-re-unexplained">{(cf.re_unmapped || []).length   /* Batch 30 · M61: name the unmapped account */
              ? t("ifrs.cf.reUnexplainedUnmapped").replace("{x}", fmt(cf.re_unexplained))
                  .replace("{acct}", `${cf.re_unmapped[0].code} ${cf.re_unmapped[0].name || ""}`.trim())
                  .replace("{advice}", t(cf.re_unmapped[0].dividend ? "ifrs.cf.reUnmappedAdviceDiv" : "ifrs.cf.reUnmappedAdviceOther"))
              : t("ifrs.cf.reUnexplained").replace("{x}", fmt(cf.re_unexplained))}</span>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <DaTaggingBanner diag={daDiag} engagementId={engagement?.id} />
        <CashFlowWarnings warnings={cf.warnings} prior={prior?.warnings} /><CashFlowInfo info={cf.info} prior={prior?.info} /><TieHeadsNote cf={cf} /><IFRSCashFlowTbInputs cf={cf} engagementId={engagement?.id} onChanged={onChanged} />
        <ColHead />
        <SectionHeader title={t("ifrs.cf.opSection")} />
        <Row label={t("ifrs.plOperatingProfit")} amount={cf.operating?.operating_profit} prior={prior?.operating?.operating_profit} testId="cf-op-profit" />
        {pairWithPrior(cf.operating?.addbacks, prior?.operating?.addbacks).map(({ line: a, prior: p }, i) => (
          <Row key={i} label={cfLabel(a, lang)} amount={a.amount} prior={p?.amount} testId={`cf-addback-${i}`} />   /* Batch 31 · item 31: label_ar on every line, prior matched by key */
        ))}
        <Row label={t("ifrs.cf.opBeforeWc")} amount={cf.operating?.subtotal_before_wc} prior={prior?.operating?.subtotal_before_wc} bold testId="cf-subtotal-wc" />
        {pairWithPrior(cf.operating?.working_capital, prior?.operating?.working_capital).map(({ line: w, prior: p }, i) => (
          <Row key={i} label={cfLabel(w, lang)} amount={w.amount} prior={p?.amount} testId={`cf-wc-${i}`} />
        ))}
        <Row label={t("ifrs.cf.cashFromOps")} amount={cf.operating?.cash_from_operations} prior={prior?.operating?.cash_from_operations} bold testId="cf-cash-from-ops" />
        <Row label={t("ifrs.cf.taxPaid")} amount={cf.operating?.tax_paid} prior={prior?.operating?.tax_paid} testId="cf-tax-paid" />
        <Row label={cfNetLabel(cf.operating, lang, t("ifrs.cf.netOp"))} amount={cf.operating?.net} prior={prior?.operating?.net} bold testId="cf-net-op" />

        <SectionHeader title={t("ifrs.cf.invSection")} />
        {pairWithPrior(cf.investing?.lines, prior?.investing?.lines).map(({ line: l, prior: p }, i) => (
          <Row key={i} label={cfLabel(l, lang)} amount={l.amount} prior={p?.amount} testId={`cf-inv-${i}`} />
        ))}
        <InterestAssumedNote investing={cf.investing} />
        <Row label={cfNetLabel(cf.investing, lang, t("ifrs.cf.netInv"))} amount={cf.investing?.net} prior={prior?.investing?.net} bold testId="cf-net-inv" />

        <SectionHeader title={t("ifrs.cf.finSection")} />
        {pairWithPrior(cf.financing?.lines, prior?.financing?.lines).map(({ line: l, prior: p }, i) => (
          <Row key={i} label={cfLabel(l, lang)} amount={l.amount} prior={p?.amount} testId={`cf-fin-${i}`} />
        ))}
        <Row label={cfNetLabel(cf.financing, lang, t("ifrs.cf.netFin"))} amount={cf.financing?.net} prior={prior?.financing?.net} bold testId="cf-net-fin" />

        <div className="grid grid-cols-[1fr_9rem_9rem] border-t bg-slate-900 px-4 py-3 text-white">
          <div className="text-sm font-semibold">{t("ifrs.cf.netChange")}</div>
          <div className="text-end num-cell font-bold" data-testid="cf-net-change">{fmt(cf.net_change_in_cash)}</div>
          <div className="text-end num-cell font-bold" data-testid="cf-net-change-py">{prior ? fmt(prior.net_change_in_cash) : ""}</div>
        </div>
        <Row label={t("ifrs.cf.cashOpening")} amount={cf.cash_opening} prior={prior?.cash_opening} testId="cf-cash-open" />
        <CashReconRows cf={cf} prior={prior} Row={Row} />
        <Row label={t("ifrs.cf.cashClosing")} amount={cf.cash_closing} prior={prior?.cash_closing} bold testId="cf-cash-close" />
        <OverdraftNote cf={cf} /><DiscontinuedCashNote cf={cf} prior={prior} />
        <div className="border-t bg-slate-50 dark:bg-slate-900/40 px-4 py-2 text-[11px] text-muted-foreground" data-testid="cf-footnote">
          {t("ifrs.cf.sfpCashPrefix").replace("{code}", "1206")} <span className="font-mono">{fmt(cf.cash_sfp_closing)}</span>
          {" · "}{t("ifrs.cf.residualPrefix")} <span className={`font-mono ${tied ? "text-emerald-700" : "text-red-700"}`}>{fmt(cf.tie_check)}</span>
          {prior ? <>{" · "}{t("ifrs.cf.priorTiePrefix").replace("{fy}", pyLabel)} <span className="font-mono">{fmt(prior.cash_sfp_closing)}</span>{" · "}{t("ifrs.cf.residualPrefix")} <span className={`font-mono ${Math.abs(prior.tie_check || 0) < 1.0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(prior.tie_check)}</span></> : null}
          {cf.prior_available === false ? <>{" · "}{t("ifrs.cf.priorNotAvailable")}</> : null}
        </div>
      </CardContent>
    </Card>
  );
}

export function ValidationRenderer({ data, engId, onChanged }) {   // exported for its jest test (PR 52 fixes 2)
  const { t, lang } = useI18n();
  if (!data) {
    return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">{t("ifrs.validation.loading")}</CardContent></Card>;
  }
  const checks = data.checks || [];
  const passed = data.passed || 0;
  const total = data.total || 0;
  const allPass = data.all_pass;
  // IFRS 18 / CF card V2 · A2 / A5: "pl" (P&L foots, TCI); PR 52 fixes 2: "restatement" (only with a posted restatement)
  const groupOrder = ["tb", "sfp", "soce", "cf", "pl", "notes", "restatement"];
  const groupLabels = {
    tb: t("ifrs.validation.groups.tb"),
    sfp: t("ifrs.validation.groups.sfp"),
    soce: t("ifrs.validation.groups.soce"),
    cf: t("ifrs.validation.groups.cf"),
    pl: t("ifrs.validation.groups.pl"),
    notes: t("ifrs.validation.groups.notes"),
    restatement: t("ifrs.validation.groups.restatement"),
  };
  const grouped = groupOrder.map((g) => ({
    group: g,
    label: groupLabels[g],
    items: checks.filter((c) => c.group === g),
  })).filter((g) => g.items.length > 0);

  return (
    <Card data-testid="validation-renderer">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">{t("ifrs.validation.title")}</CardTitle>
        <div className="flex items-center gap-2">
          <FrameworkBadge framework={data.framework} />
          {allPass
            ? <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700"
                    data-testid="validation-overall">
                <ShieldCheck className="h-3.5 w-3.5" /> {t("ifrs.validation.allPassed").replace("{total}", total)}
              </span>
            : <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-700"
                    data-testid="validation-overall">
                <ShieldAlert className="h-3.5 w-3.5" /> {t("ifrs.validation.partialPassed").replace("{passed}", passed).replace("{total}", total)}
              </span>}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <FirstYearConflictNote conflict={data.first_year_conflict} testId="validation-first-year-conflict" className="m-3" />   {/* FX2-2 */}
        <CashFlowWarnings warnings={data.warnings} testId="validation-warnings" /><CashFlowInfo info={data.info} testId="validation-info" />
        {grouped.map(({ group, label, items }) => (
          <div key={group} data-testid={`validation-group-${group}`}>
            <div className="border-b bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
              {label}
            </div>
            {items.map((c, i) => {
              const pass = c.status === "PASS";
              // PR 52 fixes 2: N/A and FLAG (outside the counts) grey / amber; NOT VERIFIED amber — never shown as a pass
              const soft = c.status === "N/A" ? "na" : (c.status === "FLAG" || c.status === "NOT VERIFIED" || c.status === "WARNING") ? "warn" : null;   // PR 53 · F2: WARNING
              const detail = (lang === "ar" && c.detail_text_ar) || c.detail_text;
              return (
                <div key={i}
                     className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b px-4 py-1.5 text-sm"
                     data-testid={`validation-check-${group}-${i}`}>
                  <div className="flex items-center gap-2">
                    {pass
                      ? <CheckCircle2 className="h-3.5 w-3.5 flex-none text-emerald-600" />
                      : soft ? <ShieldAlert className={`h-3.5 w-3.5 flex-none ${soft === "na" ? "text-slate-400" : "text-amber-600"}`} />
                      : <XCircle className="h-3.5 w-3.5 flex-none text-red-600" />}
                    <span className={pass ? "" : soft === "na" ? "text-muted-foreground" : soft ? "text-amber-800 dark:text-amber-300"
                      : "text-red-800 dark:text-red-300"}>
                      {(lang === "ar" && c.label_ar) || c.label}
                      {detail && <span className="block text-xs" data-testid={`validation-detail-${group}-${i}`}>{detail}</span>}
                      <RestatementFlagClear engId={engId} check={c} onChanged={onChanged} />
                    </span>
                  </div>
                  <div className="num-cell text-xs text-muted-foreground" title={t("ifrs.validation.residualTitle")}>
                    {fmt(c.residual)}
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                    pass
                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                      : soft === "na" ? "bg-slate-100 text-slate-600 dark:bg-slate-900/40 dark:text-slate-300"
                      : soft ? "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                      : "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300"
                  }`} data-testid={`validation-status-${group}-${i}`}>
                    {soft ? t(`ifrs.validation.status.${c.status}`) : c.status}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
        <div className="border-t bg-slate-50 dark:bg-slate-900/40 px-4 py-2 text-[11px] text-muted-foreground">
          {t("ifrs.validation.footnote")}
        </div>
      </CardContent>
    </Card>
  );
}

export function NotesRenderer({ data, engagement, onFirstYearSaved, onReload }) {   // exported for its jest test (Step 11d add-on)
  const { t, lang, dict } = useI18n();
  const [expanded, setExpanded] = useState({});
  const [editorial, setEditorial] = useState({});
  const [movements, setMovements] = useState({});
  const noteCols = useResizableColumns({ storageKey: "ifrs-notes", defaults: [90, 340, 140, 140], mins: [60, 160, 100, 100] });   // one width set for every note table, so the notes line up
  const engId = engagement?.id;

  useEffect(() => {
    if (!engId) return;
    api.get(`/fs/${engId}/note-editorial`).then((r) => setEditorial(r.data || {})).catch(() => {});
    api.get(`/fs/${engId}/note-movements`).then((r) => setMovements(r.data || {})).catch(() => {});
  }, [engId]);

  if (!data) {
    return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">{t("ifrs.notes.loading")}</CardContent></Card>;
  }
  const allNotes = asArray(data.notes);   // Step 12-2 · B: the not-presented notes go to the "Not presented" list below
  const notes = allNotes.filter((n) => n.presented !== false);   // Step 12-1 · A4: a note shown in full elsewhere
  const noteCount = notes.length;
  const withData = notes.filter((n) => n.has_data).length;
  const brokenTies = notes.filter((n) => noteTieFailed(n) ?? (n.kind === "rooted" && n.has_data   // Step 12-1 · A0
                                          && n.tie && Math.abs(n.tie.cy || 0) >= 0.01)).length;

  const KindBadge = ({ kind }) => {
    const map = {
      rooted: [t("ifrs.notes.kinds.rooted"), "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"],
      constant: [t("ifrs.notes.kinds.constant"), "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"],
      cross_cutting: [t("ifrs.notes.kinds.cross_cutting"), "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"],
    };
    const [label, cls] = map[kind] || map.constant;
    return <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase ${cls}`}>{label}</span>;
  };

  return (
    <Card data-testid="notes-renderer">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">
          {engagement.client_name}{t("ifrs.notes.titleSuffix").replace("{fy}", data.financial_year)}
        </CardTitle>
        <div className="flex items-center gap-2">
          <FrameworkBadge framework={data.framework} />
          <span className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                data-testid="notes-summary">
            {t("ifrs.notes.summary").replace("{withData}", withData).replace("{noteCount}", noteCount)}
          </span>
          {brokenTies > 0
            ? <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 text-xs text-red-700"
                    data-testid="notes-tie-badge"><XCircle className="h-3 w-3" /> {fmtPlural(brokenTies, lang, dict.plurals.tieBreaks)}</span>
            : <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"
                    data-testid="notes-tie-badge"><CheckCircle2 className="h-3 w-3" /> {t("ifrs.notes.allTie")}</span>}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Ifrs18FirstYearToggle engagement={engagement} framework={data.framework} onSaved={onFirstYearSaved} />
        {notes.filter((n) => n.presented !== false).map(renderNote)}
        <NotesNotPresented notes={allNotes.filter((n) => n.presented === false && !n.merged_into)} included={notes.filter((n) => n.presented_reason === "included")}
                           engagementId={engId} renderNote={renderNote} onChanged={onReload} />
      </CardContent>
    </Card>
  );

  function renderNote(n) {
          const isOpen = !!expanded[n.note_no];
          const emphasized = n.has_data;
          const tieCy = n.tie?.cy;
          const tieBroken = noteTieFailed(n) ?? (n.kind === "rooted" && n.has_data && tieCy != null && Math.abs(tieCy) >= 0.01);
          return (
            <div key={n.note_no} className="border-b last:border-b-0" data-testid={`note-${n.note_no}`}>
              <button
                type="button"
                onClick={() => setExpanded((e) => ({ ...e, [n.note_no]: !e[n.note_no] }))}
                className={`grid w-full grid-cols-[auto_1fr_auto_auto_auto] items-center gap-3 px-4 py-2 text-start text-sm transition-colors hover:bg-slate-50 dark:hover:bg-slate-900/40 ${
                  emphasized ? "" : "opacity-60"
                }`}
                data-testid={`note-toggle-${n.note_no}`}>
                <span className="font-mono text-[10px] tabular-nums text-muted-foreground" data-testid={`note-number-${n.note_no}`}>{n.presented === false ? "—" : `#${shownNo(n)}`}</span>
                <div className="min-w-0">
                  <div className="break-words font-medium">{noteTitle(n, lang)}</div>
                  {n.linked_fs_code && (
                    <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                      {t("ifrs.notes.linkedFs").replace("{code}", n.linked_fs_code)}
                    </div>
                  )}
                </div>
                <KindBadge kind={n.kind} />
                {n.has_data
                  ? <span className="num-cell text-xs font-semibold">{fmt(n.totals.cy)}</span>
                  : <span className="text-[10px] text-muted-foreground italic">{t("ifrs.notes.noData")}</span>}
                {n.kind === "rooted" && n.has_data && (
                  tieBroken
                    ? <Link to={`/ifrs/${engagement.id}/mapping?filter_fs=${n.linked_fs_code}&only=unmapped`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-semibold text-red-800 hover:bg-red-200 dark:bg-red-950/40 dark:text-red-300"
                            title={t("ifrs.notes.fixNowTitle").replace("{code}", n.linked_fs_code).replace("{n}", fmt(tieCy))}
                            data-testid={`note-tie-broken-${n.note_no}`}>
                        <XCircle className="h-3 w-3" /> tie {fmt(tieCy)} <ArrowRight className="h-2.5 w-2.5" />
                      </Link>
                    : <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                            data-testid={`note-tie-ok-${n.note_no}`}>
                        <CheckCircle2 className="h-3 w-3" /> ties
                      </span>
                )}
                {n.kind !== "rooted" && <span className="w-16"></span>}
              </button>
              {isOpen && (
                <div className="border-t bg-slate-50/60 px-4 py-3 dark:bg-slate-900/40" data-testid={`note-body-${n.note_no}`}>
                  {n.mpm && <MpmNoteView mpm={n.mpm} fy={data.financial_year} />}
                  <DeferredTaxOffsetText note={n} />
                  <NoteLedgerMovements note={n} />
                  {n.movement_schedule && !n.movements?.derived && (
                    <MovementSchedule
                      note={n}
                      engagementId={engId}
                      saved={movements}
                      onSaved={(key, payload) => setMovements((m) => ({ ...m, [key]: payload }))}
                    />
                  )}
                  <NoteEditorial
                    note={n}
                    engagementId={engId}
                    saved={editorial[n.code] || editorial[`N0${n.note_no}.INFO`] /* fallback for legacy */}
                    editorialByCode={editorial}
                    onSaved={(payload) => { setEditorial((e) => ({ ...e, [payload.note_code]: {
                      content: payload.content, source: payload.source,
                      updated_at: payload.updated_at, updated_by_name: payload.updated_by_name,
                    } })); onReload && onReload(); }}
                  />
                  {n.kind === "constant" && (
                    <p className="mt-3 text-[11px] text-muted-foreground">
                      {t("ifrs.notes.constantHint")}
                    </p>
                  )}
                  {n.kind === "cross_cutting" && !n.has_data && (
                    <p className="mt-3 text-[11px] text-muted-foreground">
                      {t("ifrs.notes.crossCuttingHint").replace("{example}", n.title.startsWith("Expenses") ? "N98.MAT / N98.LAB / N98.DEP" : n.title)}
                    </p>
                  )}
                  {n.key === "ifrs18_transition" && <TransitionNote note={n} />}
                  {n.kind === "restatement" && <RestatementNoteView note={n} />}
                  {n.kind !== "restatement" && n.lines.length > 0 && (
                    <ResizableTable cols={noteCols} wrap className="w-full text-xs" data-testid={`note-table-${n.note_no}`}>
                      <thead>
                        <tr className="text-start text-[10px] uppercase tracking-wider text-muted-foreground">
                          <ResizableTh index={0} className="py-1 pe-2">{t("ifrs.notes.cols.segment")}</ResizableTh>
                          <ResizableTh index={1} className="py-1 pe-2">{t("ifrs.notes.cols.description")}</ResizableTh>
                          <ResizableTh index={2} className="py-1 pe-2 text-end">FY{data.financial_year}</ResizableTh>
                          <ResizableTh index={3} className="py-1 pe-2 text-end">FY{Number(data.financial_year) - 1}<RestatedTag /></ResizableTh>
                        </tr>
                      </thead>
                      <tbody>
                        {n.lines.map((l) => (
                          <React.Fragment key={l.code}>
                            <tr className="border-t border-slate-200/60 dark:border-slate-800/60">
                              <td className="py-1 pe-2 font-mono text-[10px] text-muted-foreground">{l.code}</td>
                              <td className={`py-1 pe-2 ${l.subtotal ? "font-semibold" : ""}`}>{noteTitle(l, lang)}</td>
                              <td className="py-1 pe-2 text-end num-cell">{fmt(l.cy)}</td>
                              <td className="py-1 pe-2 text-end num-cell text-muted-foreground">{fmt(l.py)}</td>
                            </tr>
                            {asArray(l.accounts).map((a) => (
                              <tr key={a.account_code + "|" + a.account_name}
                                  className="text-[11px] text-muted-foreground">
                                <td className="py-0.5 pe-2 ps-4"></td>
                                <td className="py-0.5 pe-2 ps-2">
                                  <span className="font-mono text-[10px] me-1">{a.account_code}</span>
                                  {a.account_name}
                                </td>
                                <td className="py-0.5 pe-2 text-end num-cell">{fmt(a.cy)}</td>
                                <td className="py-0.5 pe-2 text-end num-cell">{fmt(a.py)}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                        {!n.no_grand_total && <tr className="border-t-2 border-slate-300 dark:border-slate-700 font-semibold">
                          <td className="py-1 pe-2"></td>
                          <td className="py-1 pe-2">{n.total_label ? noteTitle({ title: n.total_label, title_ar: n.total_label_ar }, lang) : t("ifrs.notes.totalNote").replace("{n}", shownNo(n))}</td>
                          <td className="py-1 pe-2 text-end num-cell">{fmt(n.totals.cy)}</td>
                          <td className="py-1 pe-2 text-end num-cell">{fmt(n.totals.py)}</td>
                        </tr>}
                        {n.tie && n.has_data && (
                          <tr className="text-[10px]">
                            <td colSpan={2} className="py-1 pe-2 text-muted-foreground">
                              {t("ifrs.notes.fsLineResidual").replace("{code}", n.linked_fs_code)}
                            </td>
                            <td className={`py-1 pe-2 text-end num-cell ${tieBroken ? "text-red-700" : "text-emerald-700"}`}>
                              {fmt(n.tie.cy)}
                            </td>
                            <td className={`py-1 pe-2 text-end num-cell ${Math.abs(n.tie.py || 0) >= 0.01 ? "text-red-700" : "text-emerald-700"}`}>
                              {fmt(n.tie.py)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </ResizableTable>
                  )}
                  <NoteFooter note={n} />
                </div>
              )}
            </div>
          );
  }
}

function LegacyIS({ is_, fy, py, t }) {
  if (!is_) return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;
  const c = is_.current, p = is_.prior;
  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">{t("ifrs.incomeStatement")} FY{fy}</CardTitle>
          <div className="text-xs text-muted-foreground">{t("ifrs.compareTo")} FY{py}</div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-2 border-b bg-slate-50 px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
            <div>{t("ifrs.item")}</div>
            <div className="grid grid-cols-2 gap-4 text-end">
              <div>FY{fy}</div><div>FY{py}</div>
            </div>
          </div>
          {[
            ["revenue", t("analytics.revenue"), c.revenue.total, p.revenue.total, false],
            ["cogs", t("ifrs.cogs"), c.cogs.total, p.cogs.total, false],
            ["gp", t("ifrs.grossProfit"), c.gross_profit, p.gross_profit, false, true],
            ["othinc", t("ifrs.plOtherOpIncome"), c.other_operating_income?.total || 0, p.other_operating_income?.total || 0, false],
            ["opex", t("ifrs.opex"), c.opex.total, p.opex.total, false],
            ["op", t("ifrs.operatingIncome"), c.operating_income, p.operating_income, false, true],
            ["ni", t("ifrs.netIncome"), c.net_income, p.net_income, false, true],
          ].map(([k, label, cv, pv, isNeg, bold]) => (
            <div key={k} className={`grid grid-cols-2 items-center border-b px-4 py-2 text-sm ${bold ? "bg-slate-50/60 dark:bg-slate-900/40 font-semibold" : ""}`} data-testid={`is-row-${k}`}>
              <div>{label}</div>
              <div className="grid grid-cols-2 gap-4 text-end num-cell">
                <div>{fmt(cv)}</div><div className="text-muted-foreground">{fmt(pv)}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card><CardHeader><CardTitle className="text-sm">{t("analytics.revenue")}</CardTitle></CardHeader><CardContent className="p-0"><StatementSection title={t("analytics.revenue")} lines={c.revenue.lines} total={c.revenue.total} /></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">{t("ifrs.cogs")}</CardTitle></CardHeader><CardContent className="p-0"><StatementSection title={t("ifrs.cogs")} lines={c.cogs.lines} total={c.cogs.total} /></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">{t("ifrs.opex")}</CardTitle></CardHeader><CardContent className="p-0"><StatementSection title={t("ifrs.opex")} lines={c.opex.lines} total={c.opex.total} /></CardContent></Card>
      </div>
    </>
  );
}

function LegacyBS({ bs, fy, py, t, periodEnd }) {
  if (!bs) return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;
  const bsC = bs.current;
  const bsP = bs.prior;
  const priorAmt = (bsSection, line) => {
    if (!bsSection) return null;
    const found = asArray(bsSection.lines).find((x) => x.code === line.code && x.name === line.name);
    return found ? found.amount : 0;
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">{t("ifrs.balanceSheet")} as at {fmtDate(periodEnd)}</CardTitle>
        <div className="flex items-center gap-2">
          <div className="text-xs text-muted-foreground">{t("ifrs.compareTo")} FY{py}</div>
          {bsC.totals && (
            bsC.totals.balanced
              ? <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" /> {t("gl.balanced")}</span>
              : <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 text-xs text-red-700"><XCircle className="h-3 w-3" /> {t("gl.unbalanced")}</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid grid-cols-2 border-b bg-slate-50 px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
          <div>{t("ifrs.item")}</div>
          <div className="grid grid-cols-2 gap-4 text-end"><div>FY{fy}</div><div>FY{py}</div></div>
        </div>
        {[
          ["asset", t("ifrs.assets"), bsC.asset, bsP?.asset],
          ["liability", t("ifrs.liabilities"), bsC.liability, bsP?.liability],
          ["equity", t("ifrs.equity"), bsC.equity, bsP?.equity],
        ].map(([k, label, cur, pri]) => (
          <div key={k} data-testid={`bs-section-${k}`}>
            <div className="border-b bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-700 dark:bg-slate-900/60 dark:text-slate-300">{label}</div>
            {cur.lines.map((l, i) => (
              <div key={i} className="grid grid-cols-2 border-b px-4 py-1.5 text-sm last:border-b-0">
                <div className="text-slate-700 dark:text-slate-300">
                  <span className="me-2 text-muted-foreground">{l.code}</span>{l.name}
                </div>
                <div className="grid grid-cols-2 gap-4 text-end num-cell">
                  <div>{fmt(l.amount)}</div><div className="text-muted-foreground">{fmt(priorAmt(pri, l))}</div>
                </div>
              </div>
            ))}
            <div className="grid grid-cols-2 border-b bg-slate-50 px-4 py-2 dark:bg-slate-900/40" data-testid={`bs-total-${k}`}>
              <div className="text-sm font-semibold">{t("ifrs.totalPrefix")}{label}</div>
              <div className="grid grid-cols-2 gap-4 text-end num-cell font-bold">
                <div>{fmt(cur.total)}</div><div className="text-muted-foreground">{fmt(pri?.total || 0)}</div>
              </div>
            </div>
          </div>
        ))}
        <div className="grid grid-cols-2 bg-slate-900 px-4 py-3 text-white">
          <div className="text-sm font-semibold">{t("ifrs.totalAssets")} vs {t("ifrs.liabPlusEquity")}</div>
          <div className="grid grid-cols-2 gap-4 text-end num-cell font-bold">
            <div>{fmt(bsC.totals.assets)} / {fmt(bsC.totals.liab_plus_equity)}</div>
            <div className="text-slate-300">
              {fmt(bsP?.totals?.assets || 0)} / {fmt(bsP?.totals?.liab_plus_equity || 0)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
